import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  Input,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { AnimationItem } from 'lottie-web';
import { SentryUtil } from '@app/shared/utils/sentry.util';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import {
  PopsyAnimationName,
  getLottieAnimationInfo,
  PopsyAnimationInfo,
} from '@app/shared/data/popsy-lottie-animations';
import { SafeStyle, DomSanitizer } from '@angular/platform-browser';
import { AppUriService } from '@app/core/services/app-uri.service';
import { AkitaLocationQuery } from '@app/akita/api/location/state/location.query';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { NgIf } from '@angular/common';

const DEFAULT_SPEED = 1;

@Component({
  selector: 'popsy-lottie-animation',
  templateUrl: './lottie-animation.template.html',
  styleUrls: ['./lottie-animation.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, SpinnerComponent, LottieComponent],
})
export class LottieAnimationComponent implements OnInit, OnChanges {
  @Input() public animation?: PopsyAnimationName | null = null;
  @Input() public country?: string | null = null;
  @Input() public speed?: number | null = null;
  @Input() public circle?: boolean | null = null;
  @Input() public playOnce?: boolean | null = null;

  public switch: 'A' | 'B';
  public animationError: boolean;
  public animationLoading: boolean;
  public animationLoopCompleted: boolean;
  private currentAnimation: string;
  private placeholderPath: string;
  public imagePlaceholder: SafeStyle;
  private animationItem: AnimationItem | null = null;
  private animationInfo: PopsyAnimationInfo | null = null;
  public lottieOptions: AnimationOptions | null = null;

  constructor(
    private readonly zone: NgZone,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly domSanitizer: DomSanitizer,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly akitaLocationQuery: AkitaLocationQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly appUriService: AppUriService
  ) {
    this.animationError = false;
    this.animationLoading = false;
    this.animationLoopCompleted = false;
    this.animationItem = null;
    this.animationInfo = null;
    this.switch = 'A';
    this.currentAnimation = '';
    this.placeholderPath = '';
    this.imagePlaceholder = this.generateImagePlaceholder();
    this.lottieOptions = null;
  }
  public ngOnInit(): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.setupAnimation();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.akitaRouterQuery.isBrowser) {
      if (changes?.animation || changes?.country) {
        this.setupAnimation();
      }

      if (changes?.speed) {
        this.zone.runOutsideAngular(() => {
          if (this.animationItem) {
            this.animationItem.setSpeed(
              this.speed || this.animationInfo?.speed || DEFAULT_SPEED
            );
          }
        });
      }
    }
  }

  public setupAnimation(): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.zone.runOutsideAngular(() => {
        const animation = this.animation;
        if (animation) {
          if (this.currentAnimation === animation) {
            return;
          }

          const animationInfo = getLottieAnimationInfo(
            animation,
            this.country || this.akitaLocationQuery?.currentLocation?.country
          );

          this.zone.run(() => {
            this.currentAnimation = animation;
            this.animationError = false;
            this.animationLoading = true;
            this.animationInfo = animationInfo;
            this.placeholderPath = this.animationInfo?.placeholder || '';
            this.imagePlaceholder = this.generateImagePlaceholder();
            this.changeDetector.markForCheck();
          });

          this.updateAnimation();

          this.zone.run(() => {
            // Switch between div A / B so it triggers a new load of the animation
            if (this.switch === 'A') {
              this.switch = 'B';
            } else {
              this.switch = 'A';
            }
            this.changeDetector.markForCheck();
          });
        }
      });
    }
  }

  public updateAnimation(): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.zone.run(() => {
        if (this.animationInfo?.path) {
          this.lottieOptions = {
            name: this.animationInfo.name,
            path: `${this.appUriService.domain}/assets/${this.animationInfo.path}`,
            autoplay: true,
            loop: true,
          };
          this.changeDetector.markForCheck();
          this.changeDetector.detectChanges();
        }
      });
    }
  }

  public animationLoaded(): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.zone.run(() => {
        this.animationLoading = false;
        this.changeDetector.markForCheck();
      });
    }
  }

  public animationCreated(animationItem: AnimationItem): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.zone.runOutsideAngular(() => {
        this.animationItem = animationItem;
        if (this.animationItem) {
          if (this.animationItem) {
            this.animationItem.setSpeed(
              this.speed || this.animationInfo?.speed || DEFAULT_SPEED
            );
          }

          this.animationItem.addEventListener(
            'data_failed',
            this.onAnimationError.bind(this)
          );

          this.animationItem.addEventListener(
            'loopComplete',
            this.loopComplete.bind(this)
          );

          this.animationItem.addEventListener(
            'loaded_images',
            this.animationLoaded.bind(this)
          );

          this.zone.run(() => {
            this.animationLoading = true;
            this.animationError = false;
            this.changeDetector.markForCheck();
          });
        }
      });
    }
  }

  public loopComplete(): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.zone.runOutsideAngular(() => {
        if (this.playOnce) {
          if (this.animationItem) {
            this.animationLoopCompleted = true;
            this.animationItem.stop();

            this.changeDetector.markForCheck();
            this.changeDetector.detectChanges();
            console.log('animation stopped', this.animationItem);
          }
        }
      });
    }
  }

  public onAnimationError($event?: any | null): void {
    if (this.akitaRouterQuery.isBrowser) {
      this.zone.run(() => {
        this.animationError = true;
        this.animationLoading = false;
        this.changeDetector.markForCheck();
      });

      this.zone.runOutsideAngular(() => {
        if ($event) {
          SentryUtil.reportException($event, false, () => (parsedError: string) => {
            this.googleAnalyticsService.appException(
              parsedError,
              'application',
              `${$event?.status || '-'}`,
              false,
              'ERROR',
              `LottieAnimationComponent<${this.animation}> -> onAnimationError`
            );
          });
        }
      });
    }
  }

  private generateImagePlaceholder(): SafeStyle {
    let bgImage = `none`;
    if (this.placeholderPath) {
      bgImage = `url('${this.placeholderPath}')`;
    }
    return this.domSanitizer.bypassSecurityTrustStyle(bgImage);
  }
}
